import React, { useEffect, useRef, useState } from "react";
import { DxTabbedContent, DxTabPanel } from "genesys-react-components";
import { Roles } from "./Roles";
import { Divisions } from "./Divisions";
import { Groups } from "./Groups";
import { WrapupCodes } from "./WrapupCodes";
import { DataTables } from "./DataTables";
import { Schedules } from "./Schedules";
import { ScheduleGroups } from "./ScheduleGroups";
import { QueuesMain } from "./QueuesMain";
import "../App.css";
import { Users } from "./Users";
import { permissionsToCheck } from "../utilities/helperUtils";
import { Policies } from "./Policies";
import { CallRoutes } from "./CallRoutes";
import { AuthOrgs } from "./AuthOrgs";
import { DIDs } from "./DIDs";
import { OAuths } from "./OAuths";
import { Integrations } from "./Integrations";
import { Prompts } from "./Prompts";
import { Flows } from "./Flows";
import { Permissions } from "./Permissions";

export function Tabs({
  divisions,
  checkedDivisions,
  pageCount,
  setPageCount,
  pageNumber,
  setPageNumber,
  onPageChange,
  activeTab,
  handleClick,
  opts,
  setOpts,
  permissions,
  org,
  tabName,
  setTabName,
  filters,
  setIsVisible,
  exportStatus,
  onExportCsv,
  selectedExportRows,
  setSelectedExportRows,
}) {
  const tabScrollShellRef = useRef(null);
  const [tabScrollState, setTabScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    hasOverflow: false,
  });

  const getTabTitlesElement = () =>
    tabScrollShellRef.current?.querySelector(".tab-titles");

  const updateTabScrollState = () => {
    const tabTitlesElement = getTabTitlesElement();

    if (!tabTitlesElement) {
      setTabScrollState({
        canScrollLeft: false,
        canScrollRight: false,
        hasOverflow: false,
      });
      return;
    }

    const maxScrollLeft =
      tabTitlesElement.scrollWidth - tabTitlesElement.clientWidth;
    const hasOverflow = maxScrollLeft > 1;
    const nextState = {
      canScrollLeft: hasOverflow && tabTitlesElement.scrollLeft > 1,
      canScrollRight:
        hasOverflow && tabTitlesElement.scrollLeft < maxScrollLeft - 1,
      hasOverflow,
    };

    setTabScrollState((currentState) => {
      if (
        currentState.canScrollLeft === nextState.canScrollLeft &&
        currentState.canScrollRight === nextState.canScrollRight &&
        currentState.hasOverflow === nextState.hasOverflow
      ) {
        return currentState;
      }

      return nextState;
    });
  };

  useEffect(() => {
    const tabTitlesElement = getTabTitlesElement();

    if (!tabTitlesElement) {
      return undefined;
    }

    let animationFrameId;
    const requestScrollStateUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateTabScrollState);
    };
    let resizeObserver;

    requestScrollStateUpdate();
    tabTitlesElement.addEventListener("scroll", requestScrollStateUpdate, {
      passive: true,
    });
    window.addEventListener("resize", requestScrollStateUpdate);

    if (window.ResizeObserver) {
      resizeObserver = new window.ResizeObserver(requestScrollStateUpdate);
      resizeObserver.observe(tabTitlesElement);
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      tabTitlesElement.removeEventListener("scroll", requestScrollStateUpdate);
      window.removeEventListener("resize", requestScrollStateUpdate);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [permissions]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTabNavigation = (direction) => {
    const tabTitlesElement = getTabTitlesElement();

    if (!tabTitlesElement) {
      return;
    }

    tabTitlesElement.scrollBy({
      left: Math.round(tabTitlesElement.clientWidth * 0.75) * direction,
      behavior: "smooth",
    });
    window.setTimeout(updateTabScrollState, 250);
  };

  const getExportRecordId = (record) =>
    record.id || record.number || [record.domain, record.entityType, record.action].join(":");

  const getSelectionProps = (exportTabName) => ({
    exportStatus,
    onExportCsv,
    selectedExportRows: selectedExportRows[exportTabName] || {},
    onClearExportSelection: () => {
      setSelectedExportRows((currentSelections) => ({
        ...currentSelections,
        [exportTabName]: {},
      }));
    },
    onToggleExportRow: (record, checked) => {
      setSelectedExportRows((currentSelections) => {
        const tabSelections = { ...(currentSelections[exportTabName] || {}) };

        if (checked) {
          tabSelections[getExportRecordId(record)] = record;
        } else {
          delete tabSelections[getExportRecordId(record)];
        }

        return {
          ...currentSelections,
          [exportTabName]: tabSelections,
        };
      });
    },
    onToggleVisibleExportRows: (records, checked) => {
      setSelectedExportRows((currentSelections) => {
        const tabSelections = { ...(currentSelections[exportTabName] || {}) };

        records.forEach((record) => {
          const recordId = getExportRecordId(record);

          if (checked) {
            tabSelections[recordId] = record;
          } else {
            delete tabSelections[recordId];
          }
        });

        return {
          ...currentSelections,
          [exportTabName]: tabSelections,
        };
      });
    },
  });

  // console.log("TABS", permissions);

  return (
    <div
      className={`tab-scroll-shell${tabScrollState.hasOverflow ? " tab-scroll-shell-overflow" : ""
        }`}
      ref={tabScrollShellRef}
    >
      {tabScrollState.hasOverflow && (
        <>
          <button
            aria-label="Scroll tab navigation left"
            className="tab-scroll-control tab-scroll-control-left"
            disabled={!tabScrollState.canScrollLeft}
            onClick={() => scrollTabNavigation(-1)}
            type="button"
          >
            <span aria-hidden="true">&lt;</span>
          </button>
          <button
            aria-label="Scroll tab navigation right"
            className="tab-scroll-control tab-scroll-control-right"
            disabled={!tabScrollState.canScrollRight}
            onClick={() => scrollTabNavigation(1)}
            type="button"
          >
            <span aria-hidden="true">&gt;</span>
          </button>
        </>
      )}
      <DxTabbedContent>
        <DxTabPanel title={<button className="tab-button">Divisions</button>}>
          <Divisions
            divisions={divisions}
            setTabName={setTabName}
            setOpts={setOpts}
            setIsVisible={setIsVisible}
            {...getSelectionProps("divisions-tab")}
          />
        </DxTabPanel>
        {permissionsToCheck("authorization:orgTrustee:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">Auth Orgs</button>}
            className="auth-orgs-tab"
          >
            <AuthOrgs
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              org={org}
              setTabName={setTabName}
              setOpts={setOpts}
              setIsVisible={setIsVisible}
            />
          </DxTabPanel>
        )}
        <DxTabPanel
          title={
            <button
              className={`tab-button ${activeTab === "Queues" ? "active" : ""}`}
              onClick={handleClick}
            >
              Queues
            </button>
          }
        >
          <QueuesMain
            activeTab={activeTab === "Queues" ? "Queues" : false}
            checkedDivisions={checkedDivisions}
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            onPageChange={onPageChange}
            setTabName={setTabName}
            opts={opts}
            setOpts={setOpts}
            filters={filters}
            setIsVisible={setIsVisible}
            {...getSelectionProps("queues-tab")}
          />
        </DxTabPanel>
        <DxTabPanel title={<button className="tab-button">Groups</button>}>
          <Groups
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            permissions={permissions}
            setTabName={setTabName}
            opts={opts}
            setOpts={setOpts}
            setIsVisible={setIsVisible}
            {...getSelectionProps("groups-tab")}
          />
        </DxTabPanel>
        {permissionsToCheck("authorization:role:view", permissions) && (
          <DxTabPanel title={<button className="tab-button">Roles</button>}>
            <Roles
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              setIsVisible={setIsVisible}
              {...getSelectionProps("roles-tab")}
            />
          </DxTabPanel>
        )}
        <DxTabPanel title={<button className="tab-button">Permissions</button>}>
          <Permissions
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setTabName={setTabName}
            opts={opts}
            setOpts={setOpts}
            setIsVisible={setIsVisible}
            {...getSelectionProps("permissions-tab")}
          />
        </DxTabPanel>
        {permissionsToCheck("routing:wrapupCode:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">Wrapup Codes</button>}
          >
            <WrapupCodes
              checkedDivisions={checkedDivisions}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              setIsVisible={setIsVisible}
              {...getSelectionProps("wrapup-codes-tab")}
            />
          </DxTabPanel>
        )}
        {permissionsToCheck("routing:wrapupCode:view", permissions) && (
          <DxTabPanel
            className="wrapup-codes-table-panel"
            title={
              <button
                className={`tab-button ${activeTab === "Wrapup Table" ? "active" : ""
                  }`}
                onClick={handleClick}
              >
                Wrapup Table
              </button>
            }
          >
            <QueuesMain
              activeTab={
                activeTab === "Wrapup Table"
                  ? "Wrapup Codes Table"
                  : false
              }
              checkedDivisions={checkedDivisions}
              pageCount={pageCount}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              filters={filters}
              setIsVisible={setIsVisible}
              {...getSelectionProps("wrapup-table-tab")}
            />
            {/* <WrapupCodesTable /> */}
          </DxTabPanel>
        )}

        {permissionsToCheck("architect:datatable:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">Data Tables</button>}
          >
            <DataTables
              checkedDivisions={checkedDivisions}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              setIsVisible={setIsVisible}
              {...getSelectionProps("schedules-tab")}
            />
          </DxTabPanel>
        )}

        {permissionsToCheck("routing:schedule:view", permissions) && (
          <DxTabPanel title={<button className="tab-button">Schedules</button>}>
            <Schedules
              checkedDivisions={checkedDivisions}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              filters={filters}
              setIsVisible={setIsVisible}
              {...getSelectionProps("schedules-tab")}
            />
          </DxTabPanel>
        )}
        {permissionsToCheck("routing:scheduleGroup:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">Schedule Groups</button>}
          >
            <ScheduleGroups
              checkedDivisions={checkedDivisions}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              opts={opts}
              setOpts={setOpts}
              setIsVisible={setIsVisible}
              {...getSelectionProps("schedule-groups-tab")}
            />
          </DxTabPanel>
        )}

        <DxTabPanel title={<button className="tab-button">Users</button>}>
          <Users
            checkedDivisions={checkedDivisions}
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setOpts={setOpts}
            setTabName={setTabName}
            filters={filters}
            setIsVisible={setIsVisible}
            {...getSelectionProps("users-tab")}
          />
        </DxTabPanel>
        <DxTabPanel
          title={<button className="tab-button">Policies</button>}
          className="policies-tab"
        >
          <Policies
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setTabName={setTabName}
            opts={opts}
            setOpts={setOpts}
            doesPermExist={permissionsToCheck(
              "recording:retentionPolicy:view",
              permissions
            )}
            setIsVisible={setIsVisible}
          />
        </DxTabPanel>
        <DxTabPanel
          title={<button className="tab-button">Call Routes</button>}
          className="call-routes-tab"
        >
          <CallRoutes
            divisions={divisions}
            checkedDivisions={checkedDivisions}
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setTabName={setTabName}
            setOpts={setOpts}
            filters={filters}
            setIsVisible={setIsVisible}
            {...getSelectionProps("call-routes-tab")}
          />
        </DxTabPanel>
        {permissionsToCheck("telephony:plugin:all", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">DIDs</button>}
            className="dids-tab"
          >
            <DIDs
              // checkedDivisions={checkedDivisions}
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setOpts={setOpts}
              opts={opts}
              setTabName={setTabName}
              setIsVisible={setIsVisible}
              {...getSelectionProps("dids-tab")}
            />
          </DxTabPanel>
        )}
        {permissionsToCheck("oauth:client:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">OAuths</button>}
            className="oauths-tab"
          >
            <OAuths
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              setIsVisible={setIsVisible}
              {...getSelectionProps("oauths-tab")}
            />
          </DxTabPanel>
        )}
        {permissionsToCheck("integrations:integration:view", permissions) && (
          <DxTabPanel
            title={<button className="tab-button">Integrations</button>}
            className="integrations-tab"
          >
            <Integrations
              setPageCount={setPageCount}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              setTabName={setTabName}
              setIsVisible={setIsVisible}
              {...getSelectionProps("integrations-tab")}
            />
          </DxTabPanel>
        )}
        <DxTabPanel
          title={<button className="tab-button">Prompts</button>}
          className="prompts-tab"
        >
          <Prompts
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setOpts={setOpts}
            opts={opts}
            filters={filters}
            setTabName={setTabName}
            setIsVisible={setIsVisible}
            {...getSelectionProps("prompts-tab")}
          />
        </DxTabPanel>
        <DxTabPanel title={<button className="tab-button">Flows</button>}>
          <Flows
            checkedDivisions={checkedDivisions}
            setPageCount={setPageCount}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            setTabName={setTabName}
            opts={opts}
            setOpts={setOpts}
            filters={filters}
            setIsVisible={setIsVisible}
            {...getSelectionProps("flows-tab")}
          />
        </DxTabPanel>
      </DxTabbedContent>
    </div>
  );
}
